import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import moment from "moment";

import Loading from "@/components/Loading";
import Page from "@/components/Page";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import ViewField from "@/components/Input/ViewField";
import VLFModal from "@/components/Modal";
import CardsTable from "@/components/CardsTable";
import StatusBadge from "@/components/StatusBadge";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PasswordIcon from "@mui/icons-material/Password";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import HowToRegIcon from "@mui/icons-material/HowToReg";

import {UserAPI} from "@/api/user";

import {ERROR_OPERATION, SUCCESSFUL_OPERATION} from "@/utils/constants";
import {usePlayers, useStaffs} from "@/utils/hooks";

function UserPage(props) {
	const {handleNotification} = props;
	const {id} = useParams();
	const navigate = useNavigate();

	const {players} = usePlayers({
		setLoading: false,
		handleNotification,
		prospect: false,
	});
	const {staffs} = useStaffs({setLoading: false, handleNotification});

	const [loading, setLoading] = useState(false);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [openResetPassword, setOpenResetPassword] = useState(false);
	const [openHandleUser, setOpenHandleUser] = useState(false);
	const [openApprove, setOpenApprove] = useState(false);
	const [userData, setUserData] = useState({});
	// Risposta del server al reset (se restituisce la nuova password la mostriamo)
	const [resetResult, setResetResult] = useState(null);
	const me = useSelector((state) => state?.user?.userName);
	const season = useSelector((state) => state?.season);

	const getUser = async () => {
		setLoading(true);
		try {
			const response = await UserAPI.getById(id);
			setUserData(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getUser();
	}, [id]);

	const deleteUser = async () => {
		setLoading(true);
		try {
			await UserAPI.delete(userData?.userName);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/settings/users");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	const resetPassword = async () => {
		setLoading(true);
		try {
			const response = await UserAPI.resetPassword({
				userName: userData?.userName,
			});
			setResetResult(
				typeof response === "string" && response ? response : "done",
			);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setOpenResetPassword(false);
			setLoading(false);
		}
	};

	const handleUser = async () => {
		setLoading(true);
		try {
			await UserAPI.setLock({
				userName: userData?.userName,
				setLock: !userData?.isLockedOut,
			});
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getUser();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleUser(false);
		}
	};

	const approveUser = async () => {
		setLoading(true);
		try {
			await UserAPI.setApproved({
				userName: userData?.userName,
				setApprove: true,
			});
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getUser();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenApprove(false);
		}
	};

	const assignedRosters = userData?.assignedRosters || [];
	// Le rose cambiano a ogni stagione: avvisa se l'utente non ne ha in quella in corso
	const noCurrentSeasonRoster =
		userData?.rosterAllAccess === false &&
		!!season?.id &&
		!assignedRosters.some((r) => r.roster?.seasonId === season.id);

	const mainConfiguration = [
		{
			value: userData?.userName,
			label: "Utente",
			size: "col-12 col-sm-6 col-lg-4",
		},
		{
			value: userData?.email,
			label: "Email",
			size: "col-12 col-sm-6 col-lg-4",
		},
		{
			value: userData?.telephone,
			label: "Telefono",
			size: "col-12 col-sm-6 col-lg-4",
		},
		{
			value: userData?.role?.name,
			label: "Ruolo",
			size: "col-12 col-sm-6 col-lg-4",
		},
	];

	if (userData?.playerId || userData?.staffId) {
		let currentItem = {};

		if (userData?.playerId) {
			currentItem = players?.filter((p) => p.id === userData.playerId)?.[0];
		} else {
			currentItem = staffs?.filter((s) => s.id === userData.staffId)?.[0];
		}

		mainConfiguration.push({
			value: currentItem
				? currentItem?.firstName + " " + currentItem?.lastName
				: "---",
			label: userData?.playerId ? "Giocatore" : "Membro dello staff",
			size: "col-12 col-sm-6 col-lg-4",
		});
	} else {
		mainConfiguration.push({
			value: "—",
			label: "Giocatore o staff",
			size: "col-12 col-sm-6 col-lg-4",
		});
	}

	mainConfiguration.push({
		value: userData?.rosterAllAccess ? "Tutte" : "Solo quelle assegnate",
		label: "Rose visibili",
		size: "col-12 col-sm-6 col-lg-4",
	});

	mainConfiguration.push(
		{
			value:
				moment(userData?.lastLoginDate).year() > 2000 &&
				moment(userData?.lastLoginDate).format("DD/MM/YYYY HH:mm"),
			label: "Ultimo accesso",
			size: "col-12 col-sm-6 col-lg-4",
		},
		{
			value:
				moment(userData?.lastPasswordChangedDate).year() > 2000 &&
				moment(userData?.lastPasswordChangedDate).format("DD/MM/YYYY HH:mm"),
			label: "Ultimo cambio password",
			size: "col-12 col-sm-6 col-lg-4",
		},
		{
			value:
				moment(userData?.lastLockoutDate).year() > 2000 &&
				moment(userData?.lastLockoutDate).format("DD/MM/YYYY HH:mm"),
			label: "Ultimo blocco",
			size: "col-12 col-sm-6 col-lg-4",
		},
		{
			value: (
				<StatusBadge
					tone={
						userData?.isLockedOut
							? "critical"
							: userData?.isApproved === false
								? "warning"
								: "good"
					}
					label={
						userData?.isLockedOut
							? "Bloccato"
							: userData?.isApproved === false
								? "Da approvare"
								: "Attivo"
					}
				/>
			),
			label: "Stato",
			size: "col-12 col-sm-6 col-lg-4",
		},
	);

	const rostersColumns = [
		{
			label: "Rosa",
			key: "roster",
			align: "start",
			customRender: (item) => item.roster?.name,
		},
		{
			label: "Stagione",
			key: "season",
			align: "start",
			customRender: (item) => item.roster?.season?.name || "—",
		},
	];

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate("/settings/users"),
			color: "info",
		},
		{
			icon: <EditIcon />,
			title: "Modifica accessi",
			onClick: () => navigate("/settings/users/" + id + "/edit"),
		},
		...(userData?.isApproved === false
			? [
					{
						icon: <HowToRegIcon />,
						title: "Approva accesso",
						variant: "outlined",
						onClick: () => setOpenApprove(true),
					},
				]
			: []),
		{
			icon: <PasswordIcon />,
			title: "Reimposta password",
			variant: "outlined",
			onClick: () => setOpenResetPassword(true),
		},
		// Non ci si blocca né si elimina il proprio account
		...(userData?.userName && userData.userName !== me
			? [
					{
						icon: userData.isLockedOut ? <LockOpenIcon /> : <LockIcon />,
						title: userData.isLockedOut ? "Sblocca accesso" : "Blocca accesso",
						variant: "outlined",
						onClick: () => setOpenHandleUser(true),
					},
					{
						icon: <DeleteIcon />,
						title: "Elimina utente",
						variant: "outlined",
						onClick: () => setOpenHandleConfirm(true),
						color: "error",
					},
				]
			: []),
	];

	const breadcrumbsElements = [{label: "Utenti", path: "/settings/users"}];

	return (
		<Page className="page-container">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={userData ? userData.userName : ""}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<div className="row no-margin">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni principali"
							content={
								<div className="row">
									<div className="col-sm-12">
										<ViewField configuration={mainConfiguration} />
									</div>
								</div>
							}
							openable={false}
						/>
					</div>
				</div>
				{!userData?.rosterAllAccess && (
					<div className="row no-margin mt-3">
						<div className="col-sm-12 padding-small">
							{noCurrentSeasonRoster && (
								<div className="mb-2">
									<StatusBadge
										tone="warning"
										label={
											"Nessuna rosa assegnata nella stagione " + season.name
										}
									/>
								</div>
							)}
							<CardsTable
								title="Rose assegnate"
								fullHeight={false}
								middleHeight
								configurationColumns={rostersColumns}
								data={assignedRosters}
							/>
						</div>
					</div>
				)}
			</div>
			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						Eliminare l'utente {userData?.userName}? Non potrà più accedere.
					</div>
				}
				confirm={() => deleteUser()}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Elimina"
			/>
			<VLFModal
				open={openResetPassword}
				title="Reimposta password"
				content={
					<div className="mt-2">
						La password di {userData?.userName} verrà reimpostata e quella
						attuale non funzionerà più.
					</div>
				}
				confirm={() => resetPassword()}
				close={() => setOpenResetPassword(false)}
				confirmLabel="Reimposta"
			/>
			<VLFModal
				open={!!resetResult}
				title="Password reimpostata"
				content={
					<div className="mt-2">
						{resetResult && resetResult !== "done" ? (
							<>
								Nuova password di {userData?.userName}:{" "}
								<b className="user-select-all">{resetResult}</b>. Comunicala
								all'utente: potrà cambiarla da "Il mio account".
							</>
						) : (
							<>
								Fatto. L'utente potrà cambiare la password da "Il mio account".
							</>
						)}
					</div>
				}
				close={() => setResetResult(null)}
			/>
			<VLFModal
				open={openApprove}
				title="Approva accesso"
				content={
					<div className="mt-2">
						{userData?.userName} potrà entrare nell'app con i permessi del suo
						ruolo.
					</div>
				}
				confirm={() => approveUser()}
				close={() => setOpenApprove(false)}
				confirmLabel="Approva"
			/>
			<VLFModal
				open={openHandleUser}
				title={userData?.isLockedOut ? "Sblocca accesso" : "Blocca accesso"}
				content={
					<div className="mt-2">
						{userData?.isLockedOut
							? `${userData?.userName} potrà di nuovo entrare nell'app.`
							: `${userData?.userName} non potrà più entrare finché non lo sblocchi.`}
					</div>
				}
				confirm={() => handleUser()}
				close={() => setOpenHandleUser(false)}
				confirmLabel={userData?.isLockedOut ? "Sblocca" : "Blocca"}
			/>
		</Page>
	);
}

export default UserPage;
